import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  RefreshCw,
  Activity,
  Server,
  Database,
  Mail,
  Shield,
  Link,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Status colors and icons
const statusConfig = {
  operational: {
    color: 'bg-green-500',
    textColor: 'text-green-600',
    bgLight: 'bg-green-50',
    borderColor: 'border-green-200',
    icon: CheckCircle,
    label: 'Operational'
  },
  degraded: {
    color: 'bg-yellow-500',
    textColor: 'text-yellow-600',
    bgLight: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    icon: AlertTriangle,
    label: 'Degraded Performance'
  },
  partial_outage: {
    color: 'bg-orange-500',
    textColor: 'text-orange-600',
    bgLight: 'bg-orange-50',
    borderColor: 'border-orange-200',
    icon: AlertTriangle,
    label: 'Partial Outage'
  },
  major_outage: {
    color: 'bg-red-500',
    textColor: 'text-red-600',
    bgLight: 'bg-red-50',
    borderColor: 'border-red-200',
    icon: XCircle,
    label: 'Major Outage'
  },
  maintenance: {
    color: 'bg-blue-500',
    textColor: 'text-blue-600',
    bgLight: 'bg-blue-50',
    borderColor: 'border-blue-200',
    icon: Clock,
    label: 'Under Maintenance'
  },
  unknown: {
    color: 'bg-gray-500',
    textColor: 'text-gray-600',
    bgLight: 'bg-gray-50',
    borderColor: 'border-gray-200',
    icon: Activity,
    label: 'Unknown'
  }
};

// Component icons
const componentIcons = {
  api: Server,
  database: Database,
  auth: Shield,
  email: Mail,
  integrations: Link
};

// Status indicator component
const StatusIndicator = ({ status, size = 'md' }) => {
  const config = statusConfig[status] || statusConfig.unknown;
  const sizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4'
  };

  return (
    <span className={`inline-block rounded-full ${config.color} ${sizeClasses[size]}`} />
  );
};

// Overall status banner
const StatusBanner = ({ status }) => {
  const config = statusConfig[status] || statusConfig.unknown;
  const Icon = config.icon;

  return (
    <div className={`${config.bgLight} ${config.borderColor} border rounded-xl p-6 mb-8`}>
      <div className="flex items-center justify-center gap-3">
        <Icon className={`w-8 h-8 ${config.textColor}`} />
        <div>
          <h2 className={`text-2xl font-bold ${config.textColor}`}>
            {config.label}
          </h2>
          <p className="text-gray-600 text-sm">
            {status === 'operational'
              ? 'All systems are operational'
              : status === 'degraded'
              ? 'Some systems are experiencing degraded performance'
              : status === 'partial_outage'
              ? 'Some systems are experiencing an outage'
              : status === 'major_outage'
              ? 'A major outage is affecting services'
              : status === 'maintenance'
              ? 'Scheduled maintenance in progress'
              : 'System status is being determined'}
          </p>
        </div>
      </div>
    </div>
  );
};

// Component status card
const ComponentCard = ({ name, componentKey, status, responseTime, details, lastChecked }) => {
  const [expanded, setExpanded] = useState(false);
  const config = statusConfig[status] || statusConfig.unknown;
  const Icon = componentIcons[componentKey] || Activity;

  return (
    <div className={`bg-white rounded-lg border ${config.borderColor} p-4 mb-3`}>
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-gray-600" />
          <span className="font-medium text-gray-800">{name}</span>
        </div>
        <div className="flex items-center gap-3">
          {responseTime && (
            <span className="text-sm text-gray-500">{responseTime}ms</span>
          )}
          <StatusIndicator status={status} />
          <span className={`text-sm font-medium ${config.textColor}`}>
            {config.label}
          </span>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Last Checked:</span>
              <p className="font-medium text-gray-700">
                {lastChecked ? new Date(lastChecked).toLocaleString() : 'N/A'}
              </p>
            </div>
            <div>
              <span className="text-gray-500">Response Time:</span>
              <p className="font-medium text-gray-700">
                {responseTime ? `${responseTime}ms` : 'N/A'}
              </p>
            </div>
            {details && (
              <div className="col-span-2">
                <span className="text-gray-500">Details:</span>
                <pre className="mt-1 text-xs bg-gray-50 p-2 rounded overflow-auto">
                  {JSON.stringify(details, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Uptime chart component
const UptimeChart = ({ data, componentName }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-sm text-gray-500">No uptime data available</div>
    );
  }

  // Generate 90 days of placeholder data if we don't have enough
  const displayData = [...data];
  while (displayData.length < 90) {
    displayData.unshift({
      date: new Date(Date.now() - displayData.length * 86400000).toISOString().split('T')[0],
      operational: 1,
      checks: 1
    });
  }

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-2">
        <span className="font-medium text-gray-700">{componentName}</span>
        <span className="text-sm text-green-600">
          {data.uptimePercentage || '100.00'}% uptime
        </span>
      </div>
      <div className="flex gap-0.5">
        {displayData.slice(-90).map((day, index) => {
          const uptime = day.checks > 0
            ? (day.operational / day.checks) * 100
            : 100;
          const color = uptime === 100
            ? 'bg-green-500'
            : uptime >= 99
            ? 'bg-green-400'
            : uptime >= 95
            ? 'bg-yellow-400'
            : 'bg-red-500';

          return (
            <div
              key={index}
              className={`h-8 w-1 rounded-sm ${color} hover:opacity-80 cursor-pointer`}
              title={`${day.date}: ${uptime.toFixed(2)}% uptime`}
            />
          );
        })}
      </div>
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>90 days ago</span>
        <span>Today</span>
      </div>
    </div>
  );
};

// Incident card
const IncidentCard = ({ incident }) => {
  const [expanded, setExpanded] = useState(false);

  const statusColors = {
    investigating: 'bg-yellow-100 text-yellow-800',
    identified: 'bg-orange-100 text-orange-800',
    monitoring: 'bg-blue-100 text-blue-800',
    resolved: 'bg-green-100 text-green-800'
  };

  const severityColors = {
    minor: 'text-yellow-600',
    major: 'text-orange-600',
    critical: 'text-red-600'
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      <div
        className="flex items-start justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[incident.status] || 'bg-gray-100 text-gray-800'}`}>
              {incident.status}
            </span>
            <span className={`text-sm font-medium ${severityColors[incident.severity] || 'text-gray-600'}`}>
              {incident.severity}
            </span>
          </div>
          <h3 className="font-medium text-gray-800">{incident.title}</h3>
          <p className="text-sm text-gray-500 mt-1">
            {new Date(incident.createdAt).toLocaleDateString()}
          </p>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </div>

      {expanded && incident.updates && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Updates</h4>
          <div className="space-y-3">
            {incident.updates.map((update, index) => (
              <div key={index} className="flex gap-3">
                <div className="w-2 h-2 mt-2 rounded-full bg-gray-400" />
                <div>
                  <p className="text-sm text-gray-700">{update.message}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(update.timestamp).toLocaleString()} - {update.status}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Metrics card
const MetricsCard = ({ metrics }) => {
  if (!metrics) return null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h3 className="font-medium text-gray-800 mb-4">System Metrics</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="text-center">
          <p className="text-2xl font-bold text-indigo-600">
            {metrics.memory?.heapUsed || 0}
          </p>
          <p className="text-sm text-gray-500">Memory (MB)</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-green-600">
            {metrics.uptime?.formatted || 'N/A'}
          </p>
          <p className="text-sm text-gray-500">Uptime</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-blue-600">
            {metrics.memory?.heapTotal || 0}
          </p>
          <p className="text-sm text-gray-500">Heap Total (MB)</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-purple-600">
            {metrics.memory?.rss || 0}
          </p>
          <p className="text-sm text-gray-500">RSS (MB)</p>
        </div>
      </div>
    </div>
  );
};

// Main Status Page component
const Status = () => {
  const [status, setStatus] = useState(null);
  const [uptime, setUptime] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      setError(null);

      const [statusRes, uptimeRes, incidentsRes, metricsRes] = await Promise.all([
        fetch(`${API_BASE}/api/status`).catch(() => null),
        fetch(`${API_BASE}/api/status/uptime`).catch(() => null),
        fetch(`${API_BASE}/api/status/incidents`).catch(() => null),
        fetch(`${API_BASE}/api/status/metrics`).catch(() => null)
      ]);

      if (statusRes && statusRes.ok) {
        const statusData = await statusRes.json();
        setStatus(statusData);
      }

      if (uptimeRes && uptimeRes.ok) {
        const uptimeData = await uptimeRes.json();
        setUptime(uptimeData);
      }

      if (incidentsRes && incidentsRes.ok) {
        const incidentsData = await incidentsRes.json();
        setIncidents(incidentsData);
      }

      if (metricsRes && metricsRes.ok) {
        const metricsData = await metricsRes.json();
        setMetrics(metricsData);
      }

      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching status:', err);
      setError('Unable to fetch system status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(fetchStatus, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [autoRefresh, fetchStatus]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Activity className="w-8 h-8 text-indigo-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">AutoHire Status</h1>
                <p className="text-sm text-gray-500">System Health Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded text-indigo-600"
                />
                Auto-refresh
              </label>
              <button
                onClick={fetchStatus}
                className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Last updated: {lastRefresh.toLocaleString()}
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-red-700">
              <XCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Overall Status */}
        <StatusBanner status={status?.status || 'unknown'} />

        {/* Component Status */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Components</h2>
          {status?.components && Object.entries(status.components).map(([key, component]) => (
            <ComponentCard
              key={key}
              componentKey={key}
              name={component.name}
              status={component.status}
              responseTime={component.responseTime}
              details={component.details}
              lastChecked={component.lastChecked}
            />
          ))}
        </section>

        {/* System Metrics */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">System Metrics</h2>
          <MetricsCard metrics={metrics} />
        </section>

        {/* Uptime History */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Uptime History (90 days)</h2>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            {uptime && Object.entries(uptime).map(([key, data]) => (
              <UptimeChart
                key={key}
                componentName={key.charAt(0).toUpperCase() + key.slice(1)}
                data={data.history || []}
              />
            ))}
            {(!uptime || Object.keys(uptime).length === 0) && (
              <p className="text-gray-500 text-center py-4">
                Uptime data will be available after the system has been running
              </p>
            )}
          </div>
        </section>

        {/* Incidents */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Recent Incidents</h2>
          {incidents && incidents.length > 0 ? (
            incidents.map((incident) => (
              <IncidentCard key={incident.id} incident={incident} />
            ))
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-600">No recent incidents</p>
              <p className="text-sm text-gray-400 mt-1">
                All systems have been operating normally
              </p>
            </div>
          )}
        </section>

        {/* Subscribe Section */}
        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Get Notified</h2>
          <p className="text-gray-600 text-sm mb-4">
            Subscribe to receive updates when there are changes to the system status.
          </p>
          <div className="flex gap-2">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
              Subscribe
            </button>
          </div>
        </section>

        {/* Footer Links */}
        <footer className="mt-8 pt-8 border-t border-gray-200 text-center">
          <div className="flex justify-center gap-6 text-sm text-gray-500">
            <a href="/" className="hover:text-indigo-600 flex items-center gap-1">
              <ExternalLink className="w-4 h-4" />
              Main Site
            </a>
            <a href="/docs" className="hover:text-indigo-600 flex items-center gap-1">
              <ExternalLink className="w-4 h-4" />
              Documentation
            </a>
            <a href="/contact" className="hover:text-indigo-600 flex items-center gap-1">
              <ExternalLink className="w-4 h-4" />
              Contact Support
            </a>
          </div>
          <p className="text-xs text-gray-400 mt-4">
            © {new Date().getFullYear()} AutoHire. All rights reserved.
          </p>
        </footer>
      </main>
    </div>
  );
};

export default Status;
